import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Shield, Sparkles, Heart, AlertTriangle,
  ShieldCheck, PhoneCall,
} from 'lucide-react';

import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import { safeJsonLd } from '../utils/seo';

import HeroSection from '../components/landing/HeroSection';
import ProgramsShowcase from '../components/landing/ProgramsShowcase';
import ProblemSection from '../components/landing/ProblemSection';
import MethodologySection from '../components/landing/MethodologySection';
import FeaturesSection from '../components/landing/FeaturesSection';
import TestimonialsSection from '../components/landing/TestimonialsSection';
import PricingSection from '../components/landing/PricingSection';
import DownloadSection from '../components/landing/DownloadSection';
import { usePublicPlans } from '../hooks/usePublicPlans';
import '../styles/pages/HomePage.css';

const HOME_PROBLEMS = [
  { icon: 'Brain',      title: 'Comportamentos compulsivos',  description: 'Hábitos repetitivos que roubam foco, energia e tempo de qualidade.' },
  { icon: 'DollarSign', title: 'Excessos com apostas',         description: 'Apostas esportivas, cassinos online e jogos de azar.' },
  { icon: 'Compass',    title: 'Dependências digitais',        description: 'Redes sociais, jogos e telas que sequestram a atenção.' },
  { icon: 'Target',     title: 'Falta de autocontrole',        description: 'Hábitos que precisam de estrutura, dados e comunidade para mudar.' },
  { icon: 'Sparkles',   title: 'Bem-estar digital',            description: 'Reconstruir uma relação saudável com tecnologia e prazer.' },
  { icon: 'Heart',      title: 'Saúde mental aplicada',        description: 'Ferramentas práticas baseadas em ciência comportamental.' },
];

// Six features keep a clean 3×2 / 2×3 grid at all common breakpoints.
const PLATFORM_FEATURES = [
  { icon: 'Activity',  title: 'Check-ins',                 description: 'Diário emocional, gatilhos e estado de espírito mapeados todo dia.',    color: 'primary'   },
  { icon: 'Compass',   title: 'Jornadas guiadas',          description: 'Plano diário do dia 1 ao dia 365 — sob medida por programa.',           color: 'secondary' },
  { icon: 'BookOpen',  title: 'Conteúdo educacional',      description: 'Artigos, vídeos e podcasts baseados em neurociência comportamental.',   color: 'tertiary'  },
  { icon: 'Sparkles',  title: 'Companion IA',              description: 'Apoio conversacional 24/7 com privacidade total.',                      color: 'warning'   },
  { icon: 'Users',     title: 'Comunidade anônima',        description: 'Grupos blindados de pessoas em jornadas iguais à sua.',                 color: 'success'   },
  { icon: 'BarChart3', title: 'Relatórios de progresso',   description: 'Métricas semanais que tornam cada pequeno avanço visível.',             color: 'primary'   },
];

// Testimonials kept anonymized and free of medical-cure claims.
const HOME_TESTIMONIALS = [
  { name: 'Lucas, 28',  role: 'Programa Freedom', avatar: '/assets/images/avatar-1.jpg', text: 'O app me deu estrutura e uma comunidade que entende o que sinto. Tenho ferramentas concretas para os dias difíceis.' },
  { name: 'Diego, 31',  role: 'Programa Balance', avatar: '/assets/images/avatar-2.jpg', text: 'Os check-ins diários e o protocolo de emergência me ajudam a perceber gatilhos antes de cair neles.' },
  { name: 'Renato, 34', role: 'Programa Freedom', avatar: '/assets/images/avatar-3.jpg', text: 'A comunidade anônima foi o que mais me ajudou — passei a me sentir menos sozinho na jornada.' },
];

const HomePage = () => {
  const navigate = useNavigate();
  const { plans } = usePublicPlans();

  const goToDownload = () => navigate('/assinar');

  return (
    <>
      <Helmet>
        <title>LifeUp — Plataforma de transformação de hábitos</title>
        <meta
          name="description"
          content="LifeUp reúne programas de bem-estar comportamental — Freedom, Balance e novos lançamentos — em uma única plataforma. Comece grátis."
        />
        <link rel="canonical" href="https://lifyup.com.br" />
        <meta property="og:title" content="LifeUp — Plataforma de transformação de hábitos" />
        <meta property="og:description" content="Programas estruturados de bem-estar comportamental e suporte de comunidade." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://lifyup.com.br" />
        <script type="application/ld+json">{safeJsonLd({
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: 'LifeUp',
          url: 'https://lifyup.com.br',
          logo: 'https://lifyup.com.br/assets/icons/logo.svg',
          description: 'Plataforma multi-programa de bem-estar comportamental e suporte de comunidade.',
        })}</script>
        <script type="application/ld+json">{safeJsonLd({
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: 'LifeUp',
          url: 'https://lifyup.com.br',
          potentialAction: {
            '@type': 'SearchAction',
            target: 'https://lifyup.com.br/blog?q={search_term_string}',
            'query-input': 'required name=search_term_string',
          },
        })}</script>
      </Helmet>

      <div className="lp-theme" style={{ background: 'var(--bg-primary)', minHeight: '100vh' }}>
        <Header />
        <main>
          <HeroSection
            eyebrow="Plataforma LifeUp"
            title="Transformando hábitos, fortalecendo vidas"
            subtitle="LifeUp reúne programas estruturados de bem-estar comportamental. Comece pelo programa que mais importa para você agora — gratuitamente."
            badges={[
              { icon: <Shield size={16} />,   label: 'Privacidade absoluta' },
              { icon: <Heart size={16} />,    label: 'Comunidade 24/7' },
              { icon: <Sparkles size={16} />, label: 'Baseado em ciência' },
            ]}
            primaryCta={{ label: 'Baixar o app', onClick: goToDownload }}
            secondaryCta={{ label: 'Conhecer os programas', onClick: () => document.getElementById('programs')?.scrollIntoView({ behavior: 'smooth' }) }}
            image="/assets/images/app-mockup.svg"
            trust={{
              avatars: [1, 2, 3, 4].map((i) => `/assets/images/avatar-${i}.jpg`),
              label: 'Pré-lançamento — em breve nas lojas',
            }}
          />

          <ProgramsShowcase
            eyebrow="Nossos programas"
            title="Escolha sua jornada"
            subtitle="Cada programa é desenhado para um tema específico, com plano diário, comunidade e ferramentas dedicadas."
          />

          <ProblemSection
            eyebrow="Onde a LifeUp atua"
            title="Temas que ajudamos a trabalhar"
            subtitle="Comportamentos diferentes compartilham padrões neuroquímicos parecidos. Nossa abordagem é estrutural — e se adapta a vários contextos."
            items={HOME_PROBLEMS}
          />

          <MethodologySection
            eyebrow="Metodologia LifeUp"
            title="Como funciona"
            subtitle="Cinco etapas que se repetem em ciclos diários — todos os programas LifeUp seguem esta arquitetura."
          />

          <FeaturesSection
            eyebrow="Recursos da plataforma"
            title="Tudo que você precisa em um só lugar"
            subtitle="Ferramentas comuns a todos os programas, disponíveis no app desde o primeiro dia."
            items={PLATFORM_FEATURES}
          />

          <TestimonialsSection
            eyebrow="Histórias compartilhadas"
            title="Depoimentos da comunidade"
            subtitle="Relatos voluntários de membros que utilizam os programas LifeUp."
            items={HOME_TESTIMONIALS}
          />

          {/* ===== Disclaimer band (LGPD / Apple 1.4.1 / Google Play Health) ===== */}
          <aside className="home-disclaimer-band" aria-label="Aviso sobre os depoimentos">
            <div className="container">
              <div className="home-disclaimer-band__icon" aria-hidden="true">
                <AlertTriangle size={20} />
              </div>
              <p className="home-disclaimer-band__body">
                <strong>Aviso:</strong> os relatos acima refletem experiências individuais de usuários e
                <strong> não representam resultados garantidos</strong>. A LifeUp é uma ferramenta de
                <strong> suporte ao bem-estar</strong> e não substitui acompanhamento médico, psicológico
                ou psiquiátrico profissional. Veja a{' '}
                <a href="/termos#disclaimer">isenção médica completa</a>.
              </p>
            </div>
          </aside>

          <PricingSection
            eyebrow="Planos"
            title="Comece grátis. Evolua quando quiser."
            description="Um plano único cobre todos os programas LifeUp — atuais e futuros. Pagamentos processados pelas lojas Apple e Google."
            plans={plans}
            onSelect={goToDownload}
          />

          {/* ===== Compliance / disclaimer panel (App Store / Google Play approval) ===== */}
          <section className="home-compliance" aria-labelledby="compliance-title">
            <div className="container">
              <header className="home-compliance__header">
                <span className="home-compliance__eyebrow">Compromisso e segurança</span>
                <h2 id="compliance-title" className="home-compliance__title">
                  Uma plataforma transparente sobre seu papel
                </h2>
                <p className="home-compliance__subtitle">
                  Acreditamos que clareza é parte da recuperação. Por isso explicamos abaixo o escopo
                  do LifeUp, requisitos de uso e como agir em emergências.
                </p>
              </header>

              <div className="home-compliance__grid">
                <article className="home-compliance-card home-compliance-card--warning">
                  <div className="home-compliance-card__icon" aria-hidden="true">
                    <AlertTriangle size={22} />
                  </div>
                  <h3>Não substitui tratamento médico</h3>
                  <p>
                    O LifeUp é uma ferramenta de <strong>suporte ao bem-estar</strong>. Não fornecemos
                    diagnósticos, prescrições ou terapia clínica. Em casos clínicos, procure
                    sempre um profissional de saúde habilitado.
                  </p>
                  <p>
                    Detalhes na <a href="/termos#disclaimer">seção 11 dos Termos</a>.
                  </p>
                </article>

                <article className="home-compliance-card home-compliance-card--info">
                  <div className="home-compliance-card__icon" aria-hidden="true">
                    <ShieldCheck size={22} />
                  </div>
                  <h3>Recomendado para 18+</h3>
                  <p>
                    Recursos de comunidade, conteúdos e programas tratam de temas sensíveis e são
                    destinados a <strong>maiores de 18 anos</strong>. Não coletamos dados de menores.
                  </p>
                  <p>
                    Saiba mais em <a href="/privacidade#menores">Privacidade de menores</a>.
                  </p>
                </article>

                <article className="home-compliance-card home-compliance-card--danger">
                  <div className="home-compliance-card__icon" aria-hidden="true">
                    <PhoneCall size={22} />
                  </div>
                  <h3>Em crise? Procure ajuda</h3>
                  <p>
                    Se você está em sofrimento intenso ou tem pensamentos de autolesão, ligue para o
                    <strong> CVV 188</strong> (24h, gratuito) ou <strong>SAMU 192</strong>. Fora do
                    Brasil, contate o serviço local de emergência.
                  </p>
                </article>
              </div>
            </div>
          </section>

          <DownloadSection />
        </main>
        <Footer />
      </div>
    </>
  );
};

export default HomePage;
